import React, { useState, useEffect, useMemo } from 'react';
import DataService from '../../XXX/DataService';
import { runeIdMapping } from '../../NewWebSocket/utils/runeIdMapping';
import './FeedbackPage.css';

type FeedbackCategory =
  | 'bug_report'
  | 'combo_suggestion'
  | 'feature_suggestion'
  | 'other';

type BugTag = 'champion' | 'item' | 'rune';

interface CategoryOption {
  value: FeedbackCategory;
  label: string;
  icon: string;
  description: string;
}

const CATEGORIES: CategoryOption[] = [
  { value: 'bug_report', icon: '🐛', label: 'Bug Report', description: 'Incorrect damage, wrong stats, broken interactions between champions, items, or runes' },
  { value: 'combo_suggestion', icon: '⚔', label: 'Combo Suggestion', description: 'Suggest a combo sequence for a champion' },
  { value: 'feature_suggestion', icon: '💡', label: 'Feature Suggestion', description: 'Ideas for new features or improvements' },
  { value: 'other', icon: '💬', label: 'Other', description: 'General feedback, UI issues, or anything else' },
];

const BUG_TAGS: { value: BugTag; label: string; hint: string }[] = [
  { value: 'champion', label: 'Champion', hint: 'Ability damage, passive, or stats' },
  { value: 'item', label: 'Item', hint: 'Item passive, stats, or on-hit' },
  { value: 'rune', label: 'Rune', hint: 'Rune proc, damage, or effect' },
];

interface SearchEntry {
  id: string;
  name: string;
  imageUrl: string;
}

const normalizeForSearch = (str: string) =>
  str.toLowerCase().replace(/['\.\s-:]/g, '');

const filterAndSort = (list: SearchEntry[], input: string, limit = 6): SearchEntry[] => {
  if (!input) return [];
  const norm = normalizeForSearch(input);
  return list
    .filter(e => {
      const normName = normalizeForSearch(e.name);
      const normId = normalizeForSearch(e.id);
      return normName.includes(norm) || normId.includes(norm);
    })
    .sort((a, b) => {
      const normA = normalizeForSearch(a.name);
      const normB = normalizeForSearch(b.name);
      if (normA.startsWith(norm) && !normB.startsWith(norm)) return -1;
      if (!normA.startsWith(norm) && normB.startsWith(norm)) return 1;
      return a.name.localeCompare(b.name);
    })
    .slice(0, limit);
};

const FeedbackPage: React.FC = () => {
  const [category, setCategory] = useState<FeedbackCategory | ''>('');
  const [bugTags, setBugTags] = useState<Set<BugTag>>(new Set());
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [championName, setChampionName] = useState('');
  const [comboSequence, setComboSequence] = useState('');
  const [itemName, setItemName] = useState('');
  const [runeName, setRuneName] = useState('');
  const [contact, setContact] = useState('');

  // Lists for autocomplete
  const [championsList, setChampionsList] = useState<SearchEntry[]>([]);
  const [itemsList, setItemsList] = useState<SearchEntry[]>([]);

  // Dropdown suggestions
  const [championSuggestions, setChampionSuggestions] = useState<SearchEntry[]>([]);
  const [itemSuggestions, setItemSuggestions] = useState<SearchEntry[]>([]);
  const [runeSuggestions, setRuneSuggestions] = useState<SearchEntry[]>([]);

  const [showChampionDropdown, setShowChampionDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [showRuneDropdown, setShowRuneDropdown] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Build rune list from the static mapping (exclude tree headers and stat shards)
  const runesList = useMemo<SearchEntry[]>(() => {
    return Object.entries(runeIdMapping)
      .filter(([, info]) => info.path) // only actual runes with icons
      .map(([id, info]) => ({
        id,
        name: info.name,
        imageUrl: `https://ddragon.leagueoflegends.com/cdn/img/perk-images/${info.path}`,
      }));
  }, []);

  // Fetch champions + items from DDragon
  useEffect(() => {
    const fetchData = async () => {
      try {
        const version = await DataService.fetchLatestVersion();

        // Champions
        const champResponse = await fetch(
          `https://ddragon.leagueoflegends.com/cdn/${version}/data/en_US/champion.json`
        );
        const champData = await champResponse.json();
        const champions: SearchEntry[] = Object.values(champData.data).map((c: any) => ({
          id: c.id,
          name: c.name,
          imageUrl: `https://ddragon.leagueoflegends.com/cdn/${version}/img/champion/${c.id}.png`,
        }));
        setChampionsList(champions);

        // Items
        const itemResponse = await fetch(
          `https://ddragon.leagueoflegends.com/cdn/${version}/data/en_US/item.json`
        );
        const itemData = await itemResponse.json();
        const items: SearchEntry[] = Object.entries(itemData.data)
          .filter(([, info]: [string, any]) => info.maps?.['11'] && !info.requiredAlly) // Summoner's Rift items only
          .map(([id, info]: [string, any]) => ({
            id,
            name: info.name,
            imageUrl: `https://ddragon.leagueoflegends.com/cdn/${version}/img/item/${id}.png`,
          }));
        setItemsList(items);
      } catch (err) {
        console.error('Failed to load data for autocomplete:', err);
      }
    };
    fetchData();
  }, []);

  // ─── Input handlers ────────────────────────────────────────────────

  const handleChampionInput = (value: string) => {
    setChampionName(value);
    const results = filterAndSort(championsList, value);
    setChampionSuggestions(results);
    setShowChampionDropdown(results.length > 0);
  };

  const handleItemInput = (value: string) => {
    setItemName(value);
    const results = filterAndSort(itemsList, value);
    setItemSuggestions(results);
    setShowItemDropdown(results.length > 0);
  };

  const handleRuneInput = (value: string) => {
    setRuneName(value);
    const results = filterAndSort(runesList, value);
    setRuneSuggestions(results);
    setShowRuneDropdown(results.length > 0);
  };

  const selectEntry = (
    entry: SearchEntry,
    setValue: (v: string) => void,
    setShow: (v: boolean) => void,
    setSuggestions: (v: SearchEntry[]) => void
  ) => {
    setValue(entry.name);
    setShow(false);
    setSuggestions([]);
  };

  const toggleBugTag = (tag: BugTag) => {
    setBugTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const handleCategoryChange = (value: FeedbackCategory) => {
    setCategory(value);
    setBugTags(new Set());
    setChampionName('');
    setComboSequence('');
    setItemName('');
    setRuneName('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!category) { setError('Please select a category'); return; }
    if (!title.trim()) { setError('Please enter a title'); return; }
    if (!description.trim()) { setError('Please enter a description'); return; }

    setSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          bugTags: category === 'bug_report' ? Array.from(bugTags) : undefined,
          championName: championName || undefined,
          comboSequence: comboSequence || undefined,
          itemName: itemName || undefined,
          runeName: runeName || undefined,
          title: title.trim(),
          description: description.trim(),
          contact: contact.trim() || undefined,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
      } else {
        setError(data.error || 'Failed to submit feedback');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Could not reach the server. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setCategory('');
    setBugTags(new Set());
    setTitle('');
    setDescription('');
    setChampionName('');
    setComboSequence('');
    setItemName('');
    setRuneName('');
    setContact('');
    setSubmitted(false);
    setError('');
  };

  // ─── Success view ──────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="feedback-page">
        <div className="feedback-container">
          <div className="feedback-success">
            <div className="success-icon">&#10003;</div>
            <h2>Thank you!</h2>
            <p>Your feedback has been submitted successfully.</p>
            <button className="feedback-btn primary" onClick={resetForm}>
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Visibility helpers ────────────────────────────────────────────

  const isBug = category === 'bug_report';
  const isComboSuggestion = category === 'combo_suggestion';
  const showChampionField = isBug ? (bugTags.has('champion') || bugTags.size === 0) : isComboSuggestion;
  const showItemField = isBug && bugTags.has('item');
  const showRuneField = isBug && bugTags.has('rune');
  const showComboField = isComboSuggestion;

  // ─── Reusable autocomplete renderer ────────────────────────────────

  const renderAutocomplete = (
    label: string,
    placeholder: string,
    value: string,
    onInput: (v: string) => void,
    suggestions: SearchEntry[],
    showDropdown: boolean,
    setShowDropdown: (v: boolean) => void,
    setSuggestions: (v: SearchEntry[]) => void,
    setValue: (v: string) => void,
  ) => (
    <div className="form-section">
      <label className="form-label">{label}</label>
      <div className="feedback-autocomplete">
        <input
          type="text"
          className="form-input"
          placeholder={placeholder}
          value={value}
          onChange={e => onInput(e.target.value)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
        />
        {showDropdown && suggestions.length > 0 && (
          <ul className="feedback-dropdown">
            {suggestions.map(entry => (
              <li
                key={entry.id}
                onMouseDown={() => selectEntry(entry, setValue, setShowDropdown, setSuggestions)}
              >
                <img src={entry.imageUrl} alt={entry.name} />
                <span>{entry.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <div className="feedback-page">
      <div className="feedback-container">
        <div className="feedback-header">
          <h1>Feedback & Bug Reports</h1>
          <p>
            Found an incorrect interaction, wrong damage value, or have a suggestion?
            Let us know and we'll look into it.
          </p>
        </div>

        <form className="feedback-form" onSubmit={handleSubmit}>
          {/* Category Selection */}
          <div className="form-section">
            <label className="form-label">Category <span className="required">*</span></label>
            <div className="category-grid">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  className={`category-card ${category === cat.value ? 'selected' : ''}`}
                  onClick={() => handleCategoryChange(cat.value)}
                >
                  <span className="category-label">{cat.icon} {cat.label}</span>
                  <span className="category-desc">{cat.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Bug Report: what's involved? */}
          {isBug && (
            <div className="form-section">
              <label className="form-label">
                What's involved? <span className="optional">(select all that apply)</span>
              </label>
              <span className="form-hint">
                Select multiple if it's an interaction between different systems, e.g. a champion + rune interaction
              </span>
              <div className="bug-tags">
                {BUG_TAGS.map(tag => (
                  <button
                    key={tag.value}
                    type="button"
                    className={`bug-tag ${bugTags.has(tag.value) ? 'active' : ''}`}
                    onClick={() => toggleBugTag(tag.value)}
                  >
                    <span className="bug-tag-label">{tag.label}</span>
                    <span className="bug-tag-hint">{tag.hint}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Champion autocomplete */}
          {showChampionField && renderAutocomplete(
            'Champion',
            "e.g. Ahri, K'Sante, Kai'Sa...",
            championName,
            handleChampionInput,
            championSuggestions,
            showChampionDropdown,
            setShowChampionDropdown,
            setChampionSuggestions,
            setChampionName,
          )}

          {/* Item autocomplete */}
          {showItemField && renderAutocomplete(
            'Item',
            'e.g. Infinity Edge, Thornmail...',
            itemName,
            handleItemInput,
            itemSuggestions,
            showItemDropdown,
            setShowItemDropdown,
            setItemSuggestions,
            setItemName,
          )}

          {/* Rune autocomplete */}
          {showRuneField && renderAutocomplete(
            'Rune',
            'e.g. Conqueror, Phase Rush...',
            runeName,
            handleRuneInput,
            runeSuggestions,
            showRuneDropdown,
            setShowRuneDropdown,
            setRuneSuggestions,
            setRuneName,
          )}

          {/* Combo Sequence (for combo suggestion) */}
          {showComboField && (
            <div className="form-section">
              <label className="form-label">Combo Sequence</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. Q W A E R"
                value={comboSequence}
                onChange={e => setComboSequence(e.target.value)}
              />
              <span className="form-hint">Use ability keys and A for auto-attack</span>
            </div>
          )}

          {/* Title */}
          <div className="form-section">
            <label className="form-label">Title <span className="required">*</span></label>
            <input
              type="text"
              className="form-input"
              placeholder={
                isBug
                  ? 'Brief summary of the bug'
                  : isComboSuggestion
                  ? 'e.g. Zed level 6 all-in combo'
                  : 'Brief summary'
              }
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={200}
            />
          </div>

          {/* Description */}
          <div className="form-section">
            <label className="form-label">Description <span className="required">*</span></label>
            <textarea
              className="form-textarea"
              placeholder={
                isBug
                  ? 'What did you expect to happen? What actually happened? Include items, levels, and any other context.'
                  : isComboSuggestion
                  ? 'Describe the combo, when to use it, what items or level it works best at, etc.'
                  : 'Describe your suggestion or feedback in detail.'
              }
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={6}
              maxLength={5000}
            />
            <span className="form-hint">{description.length}/5000</span>
          </div>

          {/* Contact (optional) */}
          <div className="form-section">
            <label className="form-label">Contact <span className="optional">(optional)</span></label>
            <input
              type="text"
              className="form-input"
              placeholder="Discord tag, email, or Reddit username if you'd like a response"
              value={contact}
              onChange={e => setContact(e.target.value)}
              maxLength={200}
            />
          </div>

          {/* Error message */}
          {error && <div className="feedback-error">{error}</div>}

          {/* Submit */}
          <div className="form-actions">
            <button
              type="submit"
              className="feedback-btn primary"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackPage;
